import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import type { Solution } from '~/store/types';

// Pattern for skin segment: lowercase alphanumeric with hyphens
const SKIN_PATTERN = /^[a-z0-9][a-z0-9-]*[a-z0-9]$|^[a-z0-9]$/;

/**
 * Normalize skin for URL: replace underscores with hyphens
 * Backend may have skins like "nui_us" but URLs should use "nui-us"
 */
export function normalizeSkin(skin: string | undefined | null): string | null {
  if (!skin) return null;
  return skin.replace(/_/g, '-');
}

// Routes that should NOT be treated as skin prefixes
const RESERVED_PATHS = new Set([
  'signin',
  'login',
  'overview',
  'marketplace',
  'dashboard',
  'products',
  'tenders',
  'orders',
  'lp-overview',
  'trades',
  'account',
  'config',
  'routes',
  'participants',
  'locations',
  'customers',
  'partners',
  'customer-requests',
  'reports',
  'consolidation',
  'deliveries',
  'users',
  'futures',
  'duplicate',
  'preview',
  'home',
]);

// Routes that should NEVER have a skin prefix (global routes)
const NO_SKIN_ROUTES = new Set(['account', 'signin', 'login', 'home']);

/**
 * Check if a path should be excluded from skin prefix redirects
 */
export function shouldExcludeFromSkinRedirect(path: string): boolean {
  const firstSegment = path.slice(1).split('/')[0];
  return NO_SKIN_ROUTES.has(firstSegment);
}

export interface SkinPathResult {
  skin: string | null;
  path: string;
}

/**
 * Extract skin from URL path
 * Examples:
 *   /hilmar/orders → { skin: 'hilmar', path: '/orders' }
 *   /orders → { skin: null, path: '/orders' }
 *   / → { skin: null, path: '/' }
 *   /hilmar → { skin: 'hilmar', path: '/' }
 */
export function extractSkinFromPath(pathname: string): SkinPathResult {
  // Guard against undefined/null pathname
  if (!pathname) {
    return { skin: null, path: '/' };
  }
  // Remove leading slash and split
  const segments = pathname.slice(1).split('/');
  const firstSegment = segments[0];

  // Check if first segment could be a skin
  if (
    firstSegment &&
    SKIN_PATTERN.test(firstSegment) &&
    !RESERVED_PATHS.has(firstSegment)
  ) {
    const remainingPath = '/' + segments.slice(1).join('/');
    return {
      skin: firstSegment,
      path: remainingPath || '/',
    };
  }

  return {
    skin: null,
    path: pathname,
  };
}

/**
 * Find solution by skin identifier
 * Matches both underscore (backend) and hyphen (URL) versions
 */
export function findSolutionBySkin(
  solutions: Solution[],
  skin: string
): Solution | undefined {
  // Try exact match first, then try converting hyphens to underscores
  return (
    solutions.find((s) => s.skin === skin) ||
    solutions.find((s) => s.skin === skin.replace(/-/g, '_'))
  );
}

/**
 * Hook: get current skin from URL
 */
export function useCurrentSkin(): string | null {
  const location = useLocation();
  return useMemo(() => {
    const { skin } = extractSkinFromPath(location.pathname);
    return skin;
  }, [location.pathname]);
}

/**
 * Hook: generate route with skin prefix
 * Returns a function that prepends the current skin to paths
 */
export function useRoute(): (path: string) => string {
  const skin = useCurrentSkin();
  return useMemo(() => (path: string) => buildSkinUrl(skin, path), [skin]);
}

/**
 * Build a URL with skin prefix
 */
export function buildSkinUrl(skin: string | null, path: string): string {
  const urlSkin = normalizeSkin(skin);
  if (!urlSkin) return path;
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  if (normalizedPath === '/') return `/${urlSkin}`;
  return `/${urlSkin}${normalizedPath}`;
}

/**
 * Background location for a modal route opened via a direct deep link, where
 * there's no prior in-app navigation to fall back to. Mirrors where the modal
 * is normally opened from (e.g. the order edit drawer overlays the orders
 * list) so the page behind it — and where closing it lands — is sensible
 * instead of the generic landing home.
 */
export function getModalBackgroundLocation(pathname: string): {
  pathname: string;
} {
  const { skin, path } = extractSkinFromPath(pathname);
  if (path.startsWith('/orders'))
    return { pathname: buildSkinUrl(skin, '/orders') };
  if (path.startsWith('/products'))
    return { pathname: buildSkinUrl(skin, '/products') };
  return { pathname: buildSkinUrl(skin, '/') };
}

/**
 * Route pattern for skin prefix (used in route definitions)
 */
export const SKIN_ROUTE_PATTERN = ':skin([a-z0-9][a-z0-9-]*[a-z0-9]|[a-z0-9])';

/**
 * Create route paths that match both skinned and non-skinned URLs
 * Used in route definitions for backward compatibility
 */
export const withSkin = (path: string): string[] => [
  `/${SKIN_ROUTE_PATTERN}${path}`,
  path,
];
